import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@supabase/supabase-js'
import type { Profile } from '../types'
import { supabase } from '../services/supabase'

interface AuthState {
  user: User | null
  profile: Profile | null
  loading: boolean
  error: string | null
}

interface AuthActions {
  setUser: (user: User | null) => void
  setProfile: (profile: Profile | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  updateProfile: (updates: Partial<Profile>) => Promise<void>
  clearError: () => void
}

type AuthStore = AuthState & AuthActions

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      profile: null,
      loading: true,
      error: null,

      // Actions
      setUser: (user) => {
        set({ user })
        if (user) {
          get().refreshProfile()
        } else {
          set({ profile: null })
        }
      },

      setProfile: (profile) => set({ profile }),

      setLoading: (loading) => set({ loading }),

      setError: (error) => set({ error }),

      clearError: () => set({ error: null }),

      signOut: async () => {
        try {
          set({ loading: true, error: null })
          const { error } = await supabase.auth.signOut()
          if (error) throw error

          set({ user: null, profile: null })
        } catch (error) {
          console.error('Sign out error:', error)
          set({ error: error instanceof Error ? error.message : 'Failed to sign out' })
        } finally {
          set({ loading: false })
        }
      },

      refreshProfile: async () => {
        const { user } = get()
        if (!user) return

        try {
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .maybeSingle()

          if (error) {
            throw error
          }

          if (!profile) {
            // If profile doesn't exist, create one
              const { data: newProfile, error: createError } = await supabase
                .from('profiles')
                .insert({
                  id: user.id,
                  username: user.email?.split('@')[0] || 'user',
                  display_name: user.user_metadata?.display_name || null,
                  level: 1,
                  total_score: 0
                })
                .select()
                .maybeSingle()

              if (createError) throw createError
              set({ profile: newProfile })
          } else {
            set({ profile })
          }
        } catch (error: any) {
          console.error('Error refreshing profile:', error)

          // Handle RLS recursion error gracefully
          if (error?.code === '42P17') {
            console.warn('RLS policy recursion detected, using fallback profile data')
            // Set a minimal profile to prevent app crash
            const fallbackProfile = {
               id: user.id,
               username: user.email?.split('@')[0] || 'user',
               display_name: user.user_metadata?.display_name || null,
               level: 1,
               total_score: 0,
               vip_level: 0,
               is_admin: false,
               words_mastered: 0,
               streak_days: 0,
               created_at: new Date().toISOString(),
               updated_at: new Date().toISOString()
             }
            set({ profile: fallbackProfile, error: null })
            return
          }

          set({ error: error instanceof Error ? error.message : 'Failed to load profile' })
        }
      },

      updateProfile: async (updates) => {
        const { user, profile } = get()
        if (!user || !profile) return

        try {
          set({ loading: true, error: null })

          const { data: updatedProfile, error } = await supabase
            .from('profiles')
            .update({
              ...updates,
              updated_at: new Date().toISOString()
            })
            .eq('id', user.id)
            .select()
            .single()

          if (error) throw error

          set({ profile: updatedProfile })
        } catch (error: any) {
          console.error('Error updating profile:', error)

          // Handle RLS recursion error gracefully
          if (error?.code === '42P17') {
            console.warn('RLS policy recursion detected during profile update')
            set({ error: 'Profile update temporarily unavailable due to database policy conflict' })
          } else {
            set({ error: error instanceof Error ? error.message : 'Failed to update profile' })
          }
        } finally {
          set({ loading: false })
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        profile: state.profile
      })
    }
  )
)

// Selectors for easier access
export const useUser = () => useAuthStore((state) => state.user)
export const useProfile = () => useAuthStore((state) => state.profile)
export const useAuthLoading = () => useAuthStore((state) => state.loading)
export const useAuthError = () => useAuthStore((state) => state.error)

// Helper hooks
export const useIsAuthenticated = () => {
  const user = useUser()
  return !!user
}

export const useUserLevel = () => {
  const profile = useProfile()
  return profile?.level || 1
}

export const useUserStats = () => {
  const profile = useProfile()
  return {
    totalScore: profile?.total_score || 0,
    level: profile?.level || 1
  }
}